<template>
    <div class="gray-0-bkg pack-product p-3 br">
        <div class="flex-level flex-top">
            <span class="pack-product-title bold">{{ product.title }}</span>
            <p class="m-0 px-4">{{ product.printableShortDescription }}</p>
            <el-tooltip v-model="lockState" :disabled="(!isLock || !isClient)" placement="bottom-start">
                <span slot="content" v-html="lockContent"></span>
                <el-switch
                    ref="pack-active-switch"
                    :disabled="isLock && isClient"
                    v-model="isActive"
                    class="pack-switch"
                    inactive-color="#565665"
                    active-color="#39CF86">
                </el-switch>
            </el-tooltip>
        </div>

        <!-- Options -->
        <div
            ref="pack-options"
            class="flex-level flex-top mt-3" 
            v-if="isActive">
            <!-- Pack Logo -->
            <div class="w-80">
                <icon-svg icon-name="card" :width="72" :height="18" viewBox="0 0 72 18">
                    <component :is="product.groups[0]"/>
                </icon-svg>
            </div>

            <!-- Pack sub plan -->
            <div v-if="isGrouped" class="ml-3">
                <el-tooltip
                    ref="sub-plan-tooltip"
                    :disabled="(!isLock || !isClient)" 
                    v-model="planState" 
                    placement="bottom-start">
                    <span slot="content" v-html="lockContent"></span>
                    <el-select 
                        ref="other-plan-select"
                        class="block w-160 mb-2" 
                        :value="product.code" 
                        :disabled="isLock && isClient"
                        @change="handlePlanChange"
                    >
                        <el-option v-for="plan in product.otherPlans" 
                            :key="plan"
                            :label="labelOtherPlan(plan) | capitalize"
                            :value="plan">
                        </el-option>
                    </el-select>
                </el-tooltip>
                <a :href="product.link" target="_blank" class="semibold">{{ $l10n.__('Comparer les plans') }}</a>
            </div>
            
            <!-- Pack amount -->
            <div 
                class="flex-level ai-center ml-3"
                :class="{'h-32': isClient}">
                <el-select 
                    v-if="!isClient"
                    class="pack-product-amount" 
                    :disabled="isFree" 
                    v-model="packPrice.unit_amount" 
                    @change="emit"
                >
                    <el-option v-for="amount in allowedAmounts" 
                        :key="amount.amount"
                        :label="amount.label"
                        :value="amount.amount">
                    </el-option>
                </el-select>
                <template v-else>
                    <span
                        ref="pack-base-price"
                        v-if="packPrice.unit_amount < basePrice && !isFree"
                        class="strike grey semibold fs-2 mr-2">
                        {{ getPriceAmountByMonth(basePrice) | floatNumber | numberformatWithOptions(numformat, true) }}
                    </span>
                    <span 
                        ref="pack-price"
                        class="fs-3 semibold"
                    >
                        {{ getPriceAmountByMonth(packPrice.unit_amount) | floatNumber | numberformatWithOptions(numformat, true) }}
                    </span>
                </template>
                <span :class="{'ml-2': !isClient}">
                    <span class="semibold" v-if="!isClient">€</span>
                    {{ $l10n.__('/mois')}}
                </span>
                <!-- It's free ! -->
                <el-checkbox 
                    ref="pack-free-checkbox"
                    v-if="!isClient" 
                    class="ml-5" 
                    v-model="isFree">
                    {{ $l10n.__('Offert') }}
                </el-checkbox>
                <el-tag 
                    effect="dark"
                    type="success"
                    size="mini"
                    class="ml-3"
                    v-if="isFree && isClient">
                    {{ $l10n.__('Offert') }}
                </el-tag>
            </div>
        </div>
    </div>
</template>

<script>
import IconSvg from 'vcomponents/icons/IconSvg.vue';
import IconRocketChart from 'vcomponents/icons/IconRocketChart.vue';
export default {
    props: {
        value: {
            type: Object,
            required: false,
            default: () => {}
        },
        product: {
            type: Object,
            required: true,
        },
        plan: {
            type: String,
            required: true
        },
        userType: {
            type: String,
            required: true
        },
        numformat: {
            type: Object,
            required: true
        },
        sourceLimitation: {
            type: Boolean,
            required: true
        },
        context: {
            type: String,
            required: false,
            default: 'new'
        }
    },
    data() {
        return {
            lockState: false,
            planState: false,
        }
    },
    computed: {
        /**
         * Get product plan configuration by current Plan selected
         */
        currentProductPlan() {
            return this.product.plans.find(plan => plan.code === this.plan)
        },
        /**
         * Get allowed pricing amount and format to use on select
         */
        allowedAmounts() {
            return this.currentProductPlan.allowedCustomPricingAmountsInCents.map(amount => {
                return {
                    label: this.isYearly ? amount / 100 / 12 : amount / 100,
                    amount
                }
            });
        },
        basePrice() {
            return this.currentProductPlan.amountInCents;
        },
        isYearly() {
            return this.currentProductPlan.billingInterval === 'year';
        } ,
        packPrice() {
            return this.value.customPricing[0];
        },
        isActive: {
            get() {
                return !!this.value.quantity;
            },
            set(value) {
                this.update(value ? 1 : 0);
            }
        },
        isFree: {
            get() {
                return this.packPrice.unit_amount === 0 && this.isActive;
            },
            set(value) {
                if(value) {
                    this.packPrice.unit_amount = 0;
                    this.update(1);
                } else {
                    this.packPrice.unit_amount = _.last(this.allowedAmounts).amount;
                    this.emit();
                }
            }
        },
        isLock() {
            return !!this.packPrice.min && (this.context !== 'renew' || this.sourceLimitation);
        },
        isGrouped() {
            return !!this.product?.groups.length;
        },
        isClient() {
            return this.userType === 'client';
        },
        lockContent() {
            return this.$l10n.__('Pour modifier les options de ce module, %s merci de contacter un commercial', '</br>');
        },
    },
    methods: {
        /**
         * Send array pricing for current product on Cart
         * Free licences can't be greater than paids, then on paid decrease, we need to update free quantity
         */
        update(value) {
            /**
             * Round quantity when its not integer, work fine with step-stricly props
             */
            if(_.isNil(value)) return;
            this.packPrice.qt = !Number.isInteger(value) ? Math.round(value) : value;

            this.emit();
        },
        emit() {
            this.$emit('input', { 
                [this.product.id]: this.value
            });
        },
        getPriceAmountByMonth(price) {
            const amount = this.isFree ? this.basePrice : price;
            return this.isYearly ? amount / 12 : amount;
        },
        labelOtherPlan(plan) {
          switch(plan) {
            case 'pack_cashflow_starter':
              return 'standard';
            case 'pack_cashflow_pro':
              return 'evolution';
            case 'pack_cashflow_business':
              return 'elite';
          }
            return plan.split('_')[2];
        },
        handlePlanChange(nextPlan) {
            if(this.product.code === nextPlan) return;
            this.$emit('changePackPlan', nextPlan);
            this.update(0);
        }
    },
    components: {
        IconSvg,
        'cashflow': IconRocketChart,
    }
}
</script>

<style lang="scss" scoped>
    .pack-product {
        .pack-product-title {
            min-width: 80px;
        }

        .pack-product-amount {
            width: 96px;
        }
    }

    .pack-switch {
        margin-left: auto;
    }
</style>